/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ApplicationRef, Injectable} from '@angular/core';

/**
 * Schedules change detection after imperative async work (Promise-based HTTP /
 * resource callbacks) that does not notify zoneless CD via signals or AsyncPipe.
 *
 * Uses markForCheck on bootstrapped roots (not ApplicationRef.tick) so we avoid
 * ExpressionChangedAfterItHasBeenCheckedError during in-flight first renders.
 */
@Injectable({
  providedIn: 'root'
})
export class AngularViewRefreshService {
  private scheduled = false;
  /** True while a scheduled markForCheck turn is in flight / applying. */
  private applying = false;

  constructor(private readonly appRef: ApplicationRef) {
  }

  schedule(): void {
    // Coalesce nested schedule() from list settle during an in-flight refresh
    // (workspace edit + DataView widgets was NG0103 via markForCheck storms).
    // If a settle lands while applying, queue one follow-up — do not drop it
    // (dropped refreshes left filter spinners spinning forever).
    if (this.scheduled) {
      return;
    }
    if (this.applying) {
      this.scheduled = true;
      setTimeout(() => {
        this.scheduled = false;
        this.schedule();
      }, 0);
      return;
    }
    this.scheduled = true;
    // Macrotask: run after Promise.then chains that mutate component state.
    setTimeout(() => {
      this.scheduled = false;
      if (this.appRef.destroyed) {
        return;
      }
      this.applying = true;
      try {
        for (const component of this.appRef.components) {
          component.changeDetectorRef.markForCheck();
        }
      } finally {
        // Clear after the CD turn this markForCheck schedules (next macrotask).
        setTimeout(() => {
          this.applying = false;
        }, 0);
      }
    }, 0);
  }
}
